import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import zoomSdk from "@zoom/appssdk";
import AppLayout from "@amzn/meridian/esm/components/app-layout";
import Box from "@amzn/meridian/esm/components/box";
import Loader from "@amzn/meridian/esm/components/loader";
import Masthead from "@amzn/meridian/esm/components/masthead";
import Theme from "@amzn/meridian/esm/components/theme";
import {
  AIMS_NOT_AVAILABLE_MESSAGE,
  AIMS_NOT_AVAILABLE_TITLE,
  AIMS_STATUS,
  FEATURE_FLAGS,
  ROLES,
  SUMMARIZE_STATES,
  TRANSCRIPTION_STATES,
  VIEWS,
  SESSION_TABS,
  PREFERENCE_SAVE_ERROR_MESSAGE,
  PANEL_WIDTH,
} from "../../constants";
import Loading from "../../components/Loading";
import AimsException from "../../components/AimsException";
import AlertModal, { ALERT_VARIANT } from "../../components/alertModal/AlertModal";
import NotAvailable from "../../components/NotAvailable";
import Transcript from "../transcript/Transcript";
import LogManager from "../../utils/logManager";
import aimsTheme from "../../styles/meridian-theme-aims.json";
import ProductFeedback from "../../components/feedback/ProductFeedback";
import { FEEDBACK_PAGE } from "../../constants";

// Sub-components
import Footer from "../assistant/agentic/Footer";
import SessionHeader from "../session/SessionHeader";
import ErrorModals from "./ErrorModals";
import DefaultView from "./DefaultView";
import SessionView from "../session/SessionView";
import ErrorToaster from "../../components/error/ErrorToaster";
import WaveformLayout from "../../components/waveformLayout/WaveformLayout";
import RtmsRejected from "./RtmsRejected";
import Summaries from "../summaries/Summaries";
import SummaryDetail from "../summaries/SummaryDetail";
import Settings from "../settings/Settings";
import { useAgenticExtensions, useRealtimeStores, useSummarizationController, useStartedByOtherBanner, useHomeNavigation } from "./home.hooks";

// Utils
import { useInMeetingInitialization } from "../../hooks/useInMeetingInitialization";
import { usePreference } from "../../context/PreferenceContext";
import { useFeatureFlags } from "../../context/FeatureFlagsContext";
import { SessionProvider } from "../../context/SessionContext";
import useAutoStartSummarization from "../../hooks/useAutoStartSummarization";
import { updateSummaryReviewers } from "../../utils/summaryReviewerUtils";

/**
 * Home - Main container for the in-meeting assistant UI.
 *
 * This component is responsible for:
 * - State management for the in-meeting assistant feature
 * - Routing to appropriate sub-views based on session state
 * - Rendering the footer navigation (End, Summaries, Settings)
 *
 * Sub-components:
 * - DefaultView: Shared view for initial state and session ended (with/without Start button)
 * - SessionView: Active session with tabs (Assistant, Agenda, Action Items)
 *
 * The RTMS/summarization state machine lives in useSummarizationController (home.hooks.js).
 *
 * @see utils/inMeetingInitializer.js for SDK init and AIMS availability
 * @see utils/rtmsSessionUtil.js for RTMS session control
 */
const Home = ({ setLastKnownContext }) => {
  const navigate = useNavigate();
  const featureFlags = useFeatureFlags();
  const { loadPreferences, isLoaded: prefsLoaded, initialAutoStart } = usePreference();

  // Loading and context state
  const [isLoading, setIsLoading] = useState(true);
  const [runningContext, setRunningContext] = useState("inMeeting");

  // User and meeting state
  const [role, setRole] = useState(null);
  const [alias, setAlias] = useState(null);
  const [meetingUUID, setMeetingUUID] = useState(null);
  const [inviteeAliases, setInviteeAliases] = useState(null);
  const [externalInvitees, setExternalInvitees] = useState(null);
  const [meetingId, setMeetingId] = useState(null);
  const [appContext, setAppContext] = useState(null);

  // AIMS status state
  const [aimsStatus, setAimsStatus] = useState(AIMS_STATUS.AVAILABLE);
  const [aimsNotAvailableMessage, setAimsNotAvailableMessage] = useState(AIMS_NOT_AVAILABLE_MESSAGE.DEFAULT);

  // Meeting/auto-start state that the summarization machine reads but doesn't own
  const [transcriptionState, setTranscriptionState] = useState(TRANSCRIPTION_STATES.DEFAULT);
  const [hasGermanyAttendee, setHasGermanyAttendee] = useState(false);
  const [leoInterviewer, setLeoInterviewer] = useState(false);
  const [autoStartMode, setAutoStartMode] = useState(null);
  const [isAutostarting, setIsAutostarting] = useState(false);

  // Summary distribution state
  const [sendToAllAttendees, setSendToAllAttendees] = useState(true);

  // Toast queue shared by all sources (agentic agent failures, preference-save failures, …).
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(0);
  const emitToast = (message) =>
    setToasts((prev) => prev.concat({ id: `toast-${++toastIdRef.current}`, message }));

  // Chatbot state
  const [chatMessages, setChatMessages] = useState([]);
  const [isChatLoading, setIsChatLoading] = useState(false);

  // Computed above the hooks (hooks can't be conditional) even though transcript
  // mode early-returns <Transcript/> and never reads this state. Without the gate,
  // an active RTMS session sets summarizeState=SUMMARIZING, which makes wsEnabled
  // true in transcript mode and opens /ws + fetches content that HIRE denies —
  // repeated 403s and spurious Hire_WsDenied alerts during correct operation.
  const isTranscriptOnly =
    aimsStatus === AIMS_STATUS.TRANSCRIPT_AVAILABLE || aimsStatus === AIMS_STATUS.TRANSCRIPT_NOT_AVAILABLE;

  // Agentic features are meaningless in transcript mode (the component early-returns
  // <Transcript/> below), so fold !isTranscriptOnly into the flag. This single definition
  // gates every agentic surface — the catalog fetch, the WebSocket, and the scheduled-agent
  // auto-activate hook — so none of them POST through the HIRE edge in transcript mode.
  const isAgenticEnabled =
    featureFlags?.isAgenticAssistantEnabled === true && !isTranscriptOnly;

  // The RTMS/summarization state machine: summarize + rtms-error state, the session/timer refs,
  // the status-change listener, and the start()/end() actions.
  const {
    summarizeState,
    isRestarting,
    setIsRestarting,
    isRtmsStartedByOther,
    rtmsRequesterName,
    meetingAgendaActive,
    meetingActionItemsActive,
    rtmsStreamId,
    summarizeClickCount,
    isProductFeedbackModalOpen,
    setIsProductFeedbackModalOpen,
    errorModals,
    setSummarizeState,
    setIsRtmsStartedByOther,
    setRtmsRequesterName,
    setMeetingAgendaActive,
    setMeetingActionItemsActive,
    setRtmsStreamId,
    rtmsStartTimestampRef,
    isEndingRef,
    start: startSummarization,
    end: endSummarization,
  } = useSummarizationController({
    meetingUUID,
    meetingId,
    role,
    alias,
    appContext,
    isLoading,
    isTranscriptOnly,
  });

  // Session identity + viewer alias + live RTMS status shared with the active-session/summary
  // subtree via SessionContext, so SessionView and its descendants read them with useSession()
  // instead of drilling props. `meetingUuid` is the canonical (lowercase) name downstream.
  const sessionValue = {
    role,
    meetingUuid: meetingUUID,
    meetingId,
    rtmsStreamId,
    alias,
    rtmsStartTime: rtmsStartTimestampRef.current,
    rtmsRequesterName,
    isRtmsStartedByOther,
    meetingAgendaActive,
    meetingActionItemsActive,
  };

  // View-navigation state machine (which view, selected summary, active session tab + handlers).
  const {
    view,
    setView,
    selectedTab,
    setSelectedTab,
    selectedSummary,
    handleSummariesClick,
    handleSelectSummary,
    handleBackToSummaries,
    handleSettingsClick,
    handleTabClick,
  } = useHomeNavigation();

  // Real-time layer: single WebSocket + the published-content and insight stores it feeds,
  // plus the on-session-start seed of action items + agenda.
  const { publishedContentStore, insightStore } = useRealtimeStores({
    meetingUUID,
    isAgenticEnabled,
    isTranscriptOnly,
    summarizeState,
    meetingAgendaActive,
    meetingActionItemsActive,
  });

  // "Another host started the session" banner.
  const { showBanner, setShowBanner } = useStartedByOtherBanner({ isRtmsStartedByOther, rtmsRequesterName });

  // Reset the transient chat/tab state at each new SUMMARIZING episode. Action items are stored
  // per meetingUuid and insightStore lives for the whole Home mount (meetingUuid is stable across
  // a stop/restart), so we intentionally do NOT clear them at the session boundary — a restart
  // keeps the still-valid rows visible instead of wiping them and re-fetching. A genuinely
  // different meeting is a fresh mount with an empty store. A new session starts on the default
  // Assistant tab — don't carry over a tab (e.g. Action Items) selected in the previous session.
  useEffect(() => {
    if (summarizeState === SUMMARIZE_STATES.SUMMARIZING) {
      setChatMessages([]);
      setIsChatLoading(false);
      publishedContentStore.clear();
      setSelectedTab(SESSION_TABS.ASSISTANT);
    }
  }, [summarizeState]);

  const handleSendToAllChange = async (checked) => {
    setSendToAllAttendees(checked);
    const success = await updateSummaryReviewers({
      rtmsStreamId,
      meetingUuid: meetingUUID,
      meetingId,
      alias,
      checked,
    });
    if (!success) {
      setSendToAllAttendees(!checked);
      return;
    }
    if (hasGermanyAttendee) {
      try {
        const message = checked
          ? "You'll receive a meeting summary when it's ready. Sharing enabled."
          : "Only organizer will receive summary. Sharing disabled. Contact them for a copy or take notes.";
        await zoomSdk.sendMessageToChat({ message });
      } catch (error) {
        LogManager.error("Error sending summary sharing message to chat:", error);
      }
    }
  };

  // Main effect: configure SDK and fetch AIMS status
  useInMeetingInitialization({
    setRunningContext,
    setLastKnownContext,
    setMeetingUUID,
    setMeetingId,
    setRole,
    setAlias,
    setAimsStatus,
    setAimsNotAvailableMessage,
    setHasGermanyAttendee,
    setLeoInterviewer,
    setAutoStartMode,
    setAppContext,
    setSummarizeState,
    setTranscriptionState,
    setIsRtmsStartedByOther,
    setRtmsRequesterName,
    setMeetingAgendaActive,
    setMeetingActionItemsActive,
    setRtmsStreamId,
    setIsAutostarting,
    setIsLoading,
    rtmsStartTimestampRef,
    loadPreferences,
    setInviteeAliases,
    setExternalInvitees,
    insightStore,
  });

  // Redirect to /client when inMainClient is detected
  useEffect(() => {
    if (runningContext === "inMainClient") {
      LogManager.info("Detected inMainClient context, redirecting to /client.");
      navigate("/client");
    }
  }, [runningContext, navigate]);

  const handleEndClick = () => {
    setView(VIEWS.MAIN);
    endSummarization();
  };

  // Auto-start summarization
  const { isAutoStarting, autoStartFailed, dismissAutoStartFailed, autoStartCompleted } = useAutoStartSummarization({
    role,
    aimsStatus,
    summarizeState,
    meetingUuid: meetingUUID,
    appContext,
    onSummarize: () => startSummarization(),
  });

  // Agentic extension catalog + enabled-agent set: fetch, scheduled auto-activate, toggle,
  // and the partial-failure / all-inactive toasts (surfaced through emitToast).
  const {
    availableExtensions,
    enabledAgentIds,
    agentsLoading,
    applyActiveAgents,
    onToggleAgent,
    onPopoverOpen,
  } = useAgenticExtensions({
    isAgenticEnabled,
    isTranscriptOnly,
    role,
    meetingUUID,
    meetingId,
    rtmsStreamId,
    summarizeState,
    emitToast,
  });

  // Loading state
  if (isLoading || runningContext === "inMainClient") {
    return <Loading />;
  }

  // Error states
  if (aimsStatus === AIMS_STATUS.TIMEOUT) {
    return <AlertModal variant={ALERT_VARIANT.AIMS_TIMEOUT} isOpen={true} />;
  }

  if (aimsStatus === AIMS_STATUS.EXCEPTION) {
    return <AimsException />;
  }

  const isNotAvailable = aimsStatus === AIMS_STATUS.NOT_AVAILABLE;

  // Transcript-only mode renders the Transcript component (no tabs) — same isTranscriptOnly the
  // hooks above gate on, so the render path and the fetch-gating path cannot disagree.
  if (isTranscriptOnly) {
    return (
      <Transcript
        aimsStatus={aimsStatus}
        role={role}
        meetingId={meetingId}
        meetingUuid={meetingUUID}
        alias={alias}
        appContext={appContext}
        initialIsRtmsStartedByOther={isRtmsStartedByOther}
        initialRtmsRequesterName={rtmsRequesterName}
        transcriptionState={transcriptionState}
        setTranscriptionState={setTranscriptionState}
        isRestarting={isRestarting}
        setIsRestarting={setIsRestarting}
        isAutostarting={isAutostarting}
        setIsAutostarting={setIsAutostarting}
        autoStartMode={autoStartMode}
        leoInterviewer={leoInterviewer}
      />
    );
  }

  // Check if End button should be visible (only for Organizer and Alternative Host when summarizing, and they started it)
  const showEndButton = (role === ROLES.ORGANIZER || role === ROLES.ALTERNATIVE_HOST) &&
                        summarizeState === SUMMARIZE_STATES.SUMMARIZING &&
                        !isRtmsStartedByOther;

  // Check if send-to-all toggle should be visible (organizer/alt host only)
  const showSendToAll = FEATURE_FLAGS.ALLOW_SUMMARY_DISTRIBUTION_TOGGLE &&
    (role === ROLES.ORGANIZER || role === ROLES.ALTERNATIVE_HOST) &&
    !isRtmsStartedByOther &&
    !!rtmsStreamId &&
    aimsStatus !== AIMS_STATUS.SUMMARY_TO_ORGANIZER;

  // Check if session is active (summarizing)
  const isSessionActive = summarizeState === SUMMARIZE_STATES.SUMMARIZING;

  // Check if RTMS is loading (starting or stopping)
  const isRtmsLoading = summarizeState === SUMMARIZE_STATES.LOADING;

  // Show loading eagerly when auto-start is expected to fire — avoids relying on async state update timing
  const isAutoStartPending = prefsLoaded &&
    initialAutoStart === true &&
    role === ROLES.ORGANIZER &&
    (aimsStatus === AIMS_STATUS.AVAILABLE || aimsStatus === AIMS_STATUS.SUMMARY_TO_ORGANIZER) &&
    summarizeState === SUMMARIZE_STATES.DEFAULT &&
    !!appContext &&
    !autoStartFailed &&
    !autoStartCompleted;

  // Check if user can start summarization (organizer or alternative host without outlook warning restriction)
  const canStartSummarization = role === ROLES.ORGANIZER ||
    (role === ROLES.ALTERNATIVE_HOST && aimsStatus !== AIMS_STATUS.SUMMARY_TO_ORGANIZER);

  return (
    <Theme palette={aimsTheme}>
      <AppLayout
        headerComponent={Masthead}
        footerComponent={((isRtmsLoading || isAutoStarting || isAutoStartPending) && !isRestarting) ? undefined : Footer}
        backgroundColor="primary"
      >
        {/* Session Tabs Header - only visible during active session */}
        {isSessionActive && (
          <SessionHeader
            view={view}
            selectedTab={selectedTab}
            onTabClick={handleTabClick}
            insightStore={insightStore}
          />
        )}

        {/* Content Area */}
        <Box width={PANEL_WIDTH}>
          {view === VIEWS.SUMMARIES ? (
            <Summaries onSelectSummary={handleSelectSummary} isSessionActive={isSessionActive} />
          ) : view === VIEWS.SUMMARY_DETAIL ? (
            <SummaryDetail summary={selectedSummary} onBack={handleBackToSummaries} isSessionActive={isSessionActive} />
          ) : view === VIEWS.SETTINGS ? (
            <Settings
              isVerboseSummariesEnabled={featureFlags?.isVerboseSummariesEnabled}
              onBack={isAgenticEnabled ? () => setView(VIEWS.MAIN) : undefined}
              onPreferenceSaveError={() => emitToast(PREFERENCE_SAVE_ERROR_MESSAGE)}
              {...(!isAgenticEnabled && {
                isRtmsStartedByOther,
                rtmsStreamId,
                meetingUuid: meetingUUID,
                meetingId,
                alias,
                summarizeState,
                aimsStatus,
                hasGermanyAttendee,
                sendToAllAttendees,
                setSendToAllAttendees,
              })}
            />
          ) : isNotAvailable ? (
            <NotAvailable title={AIMS_NOT_AVAILABLE_TITLE} message={aimsNotAvailableMessage} />
          ) : summarizeState === SUMMARIZE_STATES.REJECTED ? (
            <RtmsRejected rtmsRequesterName={rtmsRequesterName} />
          ) : (isRtmsLoading || isAutoStarting || isAutoStartPending) ? (
            isEndingRef.current ? <Loading /> : isRestarting ? <WaveformLayout icon={<Loader size="medium" />} /> : <Loading label="Starting Amazon Meetings Assistant..." />
          ) : isSessionActive ? (
            <SessionProvider value={sessionValue}>
              <SessionView
                isAgentic={isAgenticEnabled}
                selectedTab={selectedTab}
                showBanner={showBanner}
                setShowBanner={setShowBanner}
                chatMessages={chatMessages}
                setChatMessages={setChatMessages}
                isChatLoading={isChatLoading}
                setIsChatLoading={setIsChatLoading}
                enabledAgentIds={enabledAgentIds}
                availableExtensions={availableExtensions}
                onAgentsRefreshed={applyActiveAgents}
                publishedContentStore={publishedContentStore}
                insightStore={insightStore}
                inviteeAliases={inviteeAliases}
                externalInvitees={externalInvitees}
              />
            </SessionProvider>
          ) : (
            <DefaultView
              summarizeState={summarizeState}
              summarizeClickCount={summarizeClickCount}
              onStart={startSummarization}
              showStartButton={canStartSummarization}
            />
          )}
        </Box>
        {(!isRtmsLoading || isRestarting) && (
          isAgenticEnabled ? (
            <Footer
              showEndButton={showEndButton}
              onEndClick={handleEndClick}
              onSummariesClick={handleSummariesClick}
              onSettingsClick={handleSettingsClick}
              onOpenGeneral={() => setView(VIEWS.SETTINGS)}
              activeView={view}
              showSettingsPopover={isSessionActive}
              showSendToAll={showSendToAll}
              sendToAllAttendees={sendToAllAttendees}
              onSendToAllAttendeesChange={handleSendToAllChange}
              showAgentToggle={true}
              canToggleAgents={role === ROLES.ORGANIZER}
              enabledAgentIds={enabledAgentIds}
              availableExtensions={availableExtensions}
              agentsLoading={agentsLoading}
              onPopoverOpen={onPopoverOpen}
              onToggleAgent={onToggleAgent}
            />
          ) : (
            <Footer
              showEndButton={showEndButton}
              onEndClick={handleEndClick}
              onSummariesClick={handleSummariesClick}
              onSettingsClick={handleSettingsClick}
              activeView={view}
              showSettingsPopover={false}
            />
          )
        )}
      </AppLayout>
      <ErrorToaster
        toasts={toasts}
        onCloseToast={(id) => setToasts((prev) => prev.filter((t) => t.id !== id))}
      />
      <ProductFeedback
        isModalOpen={isProductFeedbackModalOpen}
        setIsModalOpen={setIsProductFeedbackModalOpen}
        usePopover={true}
        page={FEEDBACK_PAGE.SUMMARIZE_END}
      />
      {/* Error Modals */}
      <ErrorModals {...errorModals} />
      <AlertModal
        variant={ALERT_VARIANT.AUTO_START_FAILED}
        isOpen={autoStartFailed}
        onCancel={dismissAutoStartFailed}
        onStart={() => {
          dismissAutoStartFailed();
          startSummarization();
        }}
      />
    </Theme>
  );
};

export default Home;
